import React, { useState, useMemo } from 'react';
import { Search, Download, ChevronRight, AlertTriangle, Trophy, TrendingUp, XCircle, FlaskConical, Info } from 'lucide-react';
import { Button } from '../ui/Button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import {
  GroupedRow, BuildStats, SignalType, signal as calcSignal,
  fmt$, fmtR, fmtN, fmtD, fmtV, fmtDays, daysRunning, exportCSV,
} from '../../lib/campaignIntelligence';

interface CIQDashboardProps {
  allRows: GroupedRow[];
  stats: BuildStats | null;
  isDemo: boolean;
  savedWinners: SavedWinner[];
  onSignalFilter: (s: SignalType | null) => void;
  activeSignal: SignalType | null;
  dateFrom?: string;
  dateTo?: string;
}

export interface SavedWinner {
  key: string;
  account: string;
  creative: string;
  page: string;
  roi: number;
  spend: number;
  revenue: number;
  profit: number;
  signal: string;
  firstSeen: string;
  lastSeen: string;
}

const SIGNAL_CONFIG: Record<SignalType, { label: string; color: string; bg: string; border: string; icon: React.ReactNode }> = {
  win: { label: 'Winner', color: 'text-green-700 dark:text-green-400', bg: 'bg-green-100 dark:bg-green-900/40', border: 'border-green-200 dark:border-green-700', icon: <Trophy size={10} /> },
  scale: { label: 'Scale', color: 'text-yellow-700 dark:text-yellow-400', bg: 'bg-yellow-100 dark:bg-yellow-900/40', border: 'border-yellow-200 dark:border-yellow-700', icon: <TrendingUp size={10} /> },
  kill: { label: 'Kill', color: 'text-red-700 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-900/40', border: 'border-red-200 dark:border-red-700', icon: <XCircle size={10} /> },
  test: { label: 'Test', color: 'text-blue-700 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/40', border: 'border-blue-200 dark:border-blue-700', icon: <FlaskConical size={10} /> },
};

function SignalBadge({ sig }: { sig: SignalType }) {
  const c = SIGNAL_CONFIG[sig];
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded border ${c.bg} ${c.color} ${c.border}`}>
      {c.icon} {c.label}
    </span>
  );
}

export function CIQDashboard({ allRows, stats, isDemo, savedWinners, onSignalFilter, activeSignal, dateFrom, dateTo }: CIQDashboardProps) {
  const [searchQ, setSearchQ] = useState('');
  const [filterAcct, setFilterAcct] = useState('');
  const [filterPage, setFilterPage] = useState('');
  const [filterSig, setFilterSig] = useState('');
  const [sortCol, setSortCol] = useState('spend');
  const [sortDir, setSortDir] = useState(-1);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [showDiag, setShowDiag] = useState(false);

  // Totals
  const totals = useMemo(() => {
    const tSpend = allRows.reduce((s, r) => s + r.spend, 0);
    const tRev = allRows.reduce((s, r) => s + r.revenue, 0);
    const tProfit = tRev - tSpend;
    const tROI = tSpend > 0 ? tProfit / tSpend : 0;
    const tConv = allRows.reduce((s, r) => s + r.conversions, 0);
    const tClicks = allRows.reduce((s, r) => s + r.clicks, 0);
    const tCVR = tClicks > 0 ? tConv / tClicks : 0;
    const tRPL = tConv > 0 ? tRev / tConv : 0;
    const tCPM = tSpend > 0 ? allRows.reduce((s, r) => s + r.cpm * r.spend, 0) / tSpend : 0;
    const tCPC = tClicks > 0 ? tSpend / tClicks : 0;
    const tImpressions = allRows.reduce((s, r) => s + (r.cpm > 0 ? r.spend / r.cpm * 1000 : 0), 0);
    const tCTR = tImpressions > 0 ? tClicks / tImpressions : 0;
    return { tSpend, tRev, tProfit, tROI, tConv, tClicks, tCVR, tRPL, tCPM, tCPC, tCTR };
  }, [allRows]);

  // Signal counts
  const sigCounts = useMemo(() => {
    const c = { win: 0, scale: 0, kill: 0, test: 0 };
    allRows.forEach(r => { if (r.signal) c[r.signal]++; });
    return c;
  }, [allRows]);

  // Filter + sort
  const filtered = useMemo(() => {
    let base = activeSignal ? allRows.filter(r => r.signal === activeSignal) : allRows;
    return base.filter(r => {
      if (searchQ && !(r.creative + r.page + r.account).toLowerCase().includes(searchQ.toLowerCase())) return false;
      if (filterAcct && r.account !== filterAcct) return false;
      if (filterPage && r.page !== filterPage) return false;
      if (filterSig && r.signal !== filterSig) return false;
      return true;
    });
  }, [allRows, activeSignal, searchQ, filterAcct, filterPage, filterSig]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a: any, b: any) => {
      const av = a[sortCol], bv = b[sortCol];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (bv - av) * sortDir;
    });
  }, [filtered, sortCol, sortDir]);

  // Group sorted rows by page for page totals
  const pageGroups = useMemo(() => {
    const pgMap: Record<string, { account: string; page: string; rows: GroupedRow[] }> = {};
    sorted.forEach(r => {
      const k = r.account + '|||' + r.page;
      if (!pgMap[k]) pgMap[k] = { account: r.account, page: r.page, rows: [] };
      pgMap[k].rows.push(r);
    });
    return Object.values(pgMap);
  }, [sorted]);

  const accts = useMemo(() => Array.from(new Set(allRows.map(r => r.account))).sort(), [allRows]);
  const pages = useMemo(() => Array.from(new Set(allRows.map(r => r.page))).sort(), [allRows]);

  // Chart data
  const topSpend = useMemo(() => allRows.slice().sort((a, b) => b.spend - a.spend).slice(0, 6), [allRows]);
  const topROI = useMemo(() => allRows.filter(r => r.spend >= 200).sort((a, b) => b.roi - a.roi).slice(0, 6), [allRows]);
  const signalChart = useMemo(() => [
    { name: 'Winners', value: sigCounts.win, fill: '#22c55e' },
    { name: 'Scale', value: sigCounts.scale, fill: '#eab308' },
    { name: 'Kill', value: sigCounts.kill, fill: '#ef4444' },
    { name: 'Test', value: sigCounts.test, fill: '#3b82f6' },
  ], [sigCounts]);

  const handleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d * -1);
    else { setSortCol(col); setSortDir(-1); }
  };

  const toggleExpand = (key: string) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const TH = ({ col, label, title }: { col: string; label: string; title?: string }) => (
    <th
      className={`px-2.5 py-2 text-left text-[9px] font-bold uppercase tracking-wider whitespace-nowrap cursor-pointer select-none transition-colors hover:text-foreground ${
        sortCol === col ? 'text-primary' : 'text-muted-foreground'
      }`}
      onClick={() => handleSort(col)}
      title={title}
    >
      {label}{sortCol === col && (sortDir < 0 ? ' ↓' : ' ↑')}
    </th>
  );

  return (
    <div>
      {/* Demo note */}
      {isDemo && (
        <div className="flex items-center gap-2 px-3 py-2 mb-4 rounded-lg border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-950/30 text-yellow-700 dark:text-yellow-400 text-xs">
          <AlertTriangle size={14} /> Demo mode — showing synthetic data. Use Import Data to load real files.
        </div>
      )}

      {/* Date range display */}
      {dateFrom && dateTo && !isDemo && (
        <div className="flex items-center gap-2 px-3 py-2 mb-4 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 text-xs">
          <Info size={14} />
          Data range: <b>{new Date(dateFrom).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</b> → <b>{new Date(dateTo).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</b>
        </div>
      )}

      {/* Match diagnostics bar */}
      {stats && (
        <div className="bg-card border border-border rounded-lg px-4 py-2.5 mb-4 text-xs flex items-center gap-4 flex-wrap">
          <span>📋 <b>{stats.total}</b> Meta rows → <b>{stats.total - stats.zeroSpend}</b> with spend</span>
          <span className={stats.noRT === 0 ? 'text-green-600 dark:text-green-400' : stats.noRT < 3 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}>
            🔗 <b>{stats.total - stats.zeroSpend - stats.noRT}/{stats.total - stats.zeroSpend}</b> matched
            (<b>{stats.total - stats.zeroSpend > 0 ? Math.round((stats.total - stats.zeroSpend - stats.noRT) / (stats.total - stats.zeroSpend) * 100) : 0}%</b>)
          </span>
          {stats.noRT > 0 ? (
            <button onClick={() => setShowDiag(true)} className="text-red-600 dark:text-red-400 underline cursor-pointer">
              ⚠️ {stats.noRT} unmatched — see details
            </button>
          ) : (
            <span className="text-green-600 dark:text-green-400">✓ All campaigns matched</span>
          )}
        </div>
      )}

      {/* Signal sidebar buttons */}
      <div className="flex gap-2 mb-4">
        {(['win', 'scale', 'kill', 'test'] as SignalType[]).map(s => {
          const c = SIGNAL_CONFIG[s];
          const active = activeSignal === s;
          return (
            <button
              key={s}
              onClick={() => onSignalFilter(active ? null : s)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${
                active
                  ? `${c.bg} ${c.color} ${c.border}`
                  : 'border-border bg-card text-muted-foreground hover:bg-muted'
              }`}
            >
              {c.icon} {c.label} <span className="font-bold">{sigCounts[s]}</span>
            </button>
          );
        })}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-5 gap-2.5 mb-4">
        <KPI label="Total Spend" value={fmt$(totals.tSpend)} color="blue" sub="all accounts" />
        <KPI label="Total Revenue" value={fmt$(totals.tRev)} color="green" sub="all campaigns" />
        <KPI label="Total Profit" value={(totals.tProfit >= 0 ? '' : '-') + fmt$(Math.abs(totals.tProfit))} color={totals.tProfit >= 0 ? 'green' : 'red'} sub="revenue − spend" />
        <KPI label="Blended ROI" value={fmtR(totals.tROI)} color={totals.tROI >= 0 ? 'green' : 'red'} sub="weighted avg" />
        <KPI label="Total Conv" value={fmtV(totals.tConv)} color="purple" sub="total conversions" />
        <KPI label="RPL" value={fmt$(totals.tRPL)} color="yellow" sub="revenue per lead" />
        <KPI label="Avg CPM" value={fmt$(totals.tCPM)} color="cyan" sub="spend-weighted avg" />
        <KPI label="Avg CPC" value={fmt$(totals.tCPC)} color="blue" sub="spend ÷ link clicks" />
        <KPI label="Avg CTR" value={fmtR(totals.tCTR)} color="purple" sub="clicks ÷ impressions" />
        <KPI label="Blended CVR" value={fmtR(totals.tCVR)} color="green" sub="conv ÷ clicks" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {/* Top by Spend */}
        <div className="bg-card border border-border rounded-lg p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-3">Top by Spend</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={topSpend} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v: number) => '$' + (v / 1000).toFixed(0) + 'k'} />
              <YAxis type="category" dataKey="creative" width={90} tick={{ fontSize: 9 }} />
              <Tooltip formatter={(v: number) => fmt$(v)} />
              <Bar dataKey="spend" radius={[0, 3, 3, 0]}>
                {topSpend.map((_, i) => <Cell key={i} fill={['#3b82f6', '#22c55e', '#a855f7', '#eab308', '#ef4444', '#06b6d4'][i] || '#3b82f6'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top ROI */}
        <div className="bg-card border border-border rounded-lg p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-3">Top ROI (≥$200 spend)</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={topROI} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v: number) => (v * 100).toFixed(0) + '%'} />
              <YAxis type="category" dataKey="creative" width={90} tick={{ fontSize: 9 }} />
              <Tooltip formatter={(v: number) => fmtR(v)} />
              <Bar dataKey="roi" radius={[0, 3, 3, 0]}>
                {topROI.map((r, i) => <Cell key={i} fill={r.roi >= 0.5 ? '#22c55e' : r.roi >= 0.1 ? '#eab308' : '#ef4444'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Signals */}
        <div className="bg-card border border-border rounded-lg p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-3">Performance Signals</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={signalChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                {signalChart.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            className="pl-8 pr-3 py-1.5 text-xs rounded-md border border-border bg-card text-foreground outline-none focus:border-primary min-w-[190px]"
            placeholder="Search creative / page / account…"
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
          />
        </div>
        <select className="px-2.5 py-1.5 text-xs rounded-md border border-border bg-card text-foreground outline-none" value={filterAcct} onChange={e => setFilterAcct(e.target.value)}>
          <option value="">All Accounts</option>
          {accts.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className="px-2.5 py-1.5 text-xs rounded-md border border-border bg-card text-foreground outline-none" value={filterPage} onChange={e => setFilterPage(e.target.value)}>
          <option value="">All Pages</option>
          {pages.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select className="px-2.5 py-1.5 text-xs rounded-md border border-border bg-card text-foreground outline-none" value={filterSig} onChange={e => setFilterSig(e.target.value)}>
          <option value="">All Signals</option>
          <option value="win">Winners</option>
          <option value="scale">Scale</option>
          <option value="kill">Kill</option>
          <option value="test">Test</option>
        </select>
        <select className="px-2.5 py-1.5 text-xs rounded-md border border-border bg-card text-foreground outline-none" value={sortCol} onChange={e => { setSortCol(e.target.value); setSortDir(-1); }}>
          <option value="spend">↓ Spend</option>
          <option value="revenue">↓ Revenue</option>
          <option value="conversions">↓ Conv</option>
          <option value="roi">↓ ROI</option>
          <option value="profit">↓ Profit</option>
          <option value="days">↓ Days</option>
          <option value="cpm">↓ CPM</option>
          <option value="cpulc">↓ CPC</option>
          <option value="cvr">↓ CVR</option>
          <option value="rpl">↓ RPL</option>
        </select>
        <div className="flex-1" />
        <span className="text-[10px] font-mono text-muted-foreground"><b>{sorted.length}</b> rows</span>
        <Button variant="outline" size="sm" onClick={() => exportCSV(allRows)}>
          <Download size={13} className="mr-1" /> Export CSV
        </Button>
      </div>

      {/* Main Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-muted/50">
                <th className="w-[26px] px-2 py-2"></th>
                <th className="px-2.5 py-2 text-left text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Account</th>
                <th className="px-2.5 py-2 text-left text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Creative</th>
                <th className="px-2.5 py-2 text-left text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Page</th>
                <th className="px-2.5 py-2 text-center text-[9px] font-bold uppercase tracking-wider text-muted-foreground" title="Campaigns">#</th>
                <TH col="days" label="Days" title="Days running since campaign launch date" />
                <TH col="spend" label="Spend" />
                <TH col="revenue" label="Revenue" />
                <TH col="conversions" label="Conv" title="Total Conversions" />
                <TH col="profit" label="Profit" />
                <TH col="roi" label="ROI" />
                <TH col="cpulc" label="$/Click" title="Cost per Unique Link Click" />
                <TH col="cpm" label="CPM" />
                <TH col="cpr" label="CPR" title="Cost per Result" />
                <TH col="rpl" label="RPL" title="Revenue per Lead" />
                <TH col="lpctr" label="LP CTR" title="LP CTR = RedTrack LP Clicks ÷ Meta Unique Link Clicks" />
                <th className="px-2.5 py-2 text-left text-[9px] font-bold uppercase tracking-wider text-muted-foreground" title="Offer Page CPC = Spend ÷ LP Clicks">Op CPC</th>
                <TH col="epc" label="Op EPC" title="Offer Page EPC" />
                <TH col="cvr" label="CVR" title="Conversion Rate" />
                <th className="px-2.5 py-2 text-left text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Signal</th>
              </tr>
            </thead>
            <tbody>
              {pageGroups.map((pg) => {
                const ptS = pg.rows.reduce((s, r) => s + r.spend, 0);
                const ptR = pg.rows.reduce((s, r) => s + r.revenue, 0);
                const ptP = ptR - ptS;
                const ptROI = ptS > 0 ? ptP / ptS : 0;

                return (
                  <React.Fragment key={pg.account + '|||' + pg.page}>
                    {pg.rows.map((r) => {
                      const key = r.account + '|||' + r.creative + '|||' + r.page;
                      const open = expandedKeys.has(key);
                      const rd = daysRunning(r.launchDate);
                      return (
                        <React.Fragment key={key}>
                          <tr
                            className="cursor-pointer hover:bg-muted/30 transition-colors border-t border-border/30"
                            onClick={() => toggleExpand(key)}
                          >
                            <td className="px-2 py-2">
                              <ChevronRight size={12} className={`text-muted-foreground transition-transform ${open ? 'rotate-90 text-primary' : ''}`} />
                            </td>
                            <td className="px-2.5 py-2 text-muted-foreground text-[11px] max-w-[110px] truncate" title={r.account}>{r.account}</td>
                            <td className="px-2.5 py-2 font-mono text-[11px] text-primary max-w-[150px] truncate" title={r.creative}>{r.creative}</td>
                            <td className="px-2.5 py-2 font-semibold max-w-[120px] truncate" title={r.page}>{r.page}</td>
                            <td className="px-2.5 py-2 text-center font-mono text-[11px] text-muted-foreground">{r.count}</td>
                            <td className="px-2.5 py-2 text-center font-mono text-[11px] text-muted-foreground" title={r.launchDate ? r.launchDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : 'No date found'}>{fmtDays(rd)}</td>
                            <td className="px-2.5 py-2 font-mono">{fmt$(r.spend)}</td>
                            <td className="px-2.5 py-2 font-mono">{fmt$(r.revenue)}</td>
                            <td className="px-2.5 py-2 font-mono">{fmtV(r.conversions)}</td>
                            <td className={`px-2.5 py-2 font-mono ${r.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{fmt$(r.profit)}</td>
                            <td className={`px-2.5 py-2 font-mono ${r.roi >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{fmtR(r.roi)}</td>
                            <td className="px-2.5 py-2 font-mono">{fmt$(r.cpulc)}</td>
                            <td className="px-2.5 py-2 font-mono">{fmt$(r.cpm)}</td>
                            <td className="px-2.5 py-2 font-mono">{fmt$(r.cpr)}</td>
                            <td className="px-2.5 py-2 font-mono">{fmt$(r.rpl)}</td>
                            <td className="px-2.5 py-2 font-mono">{fmtN(r.lpctr)}</td>
                            <td className="px-2.5 py-2 font-mono">{fmtD(r.opCpc)}</td>
                            <td className="px-2.5 py-2 font-mono">{fmtD(r.epc)}</td>
                            <td className="px-2.5 py-2 font-mono">{fmtN(r.cvr)}</td>
                            <td className="px-2.5 py-2">{r.signal && <SignalBadge sig={r.signal} />}</td>
                          </tr>

                          {/* Expanded sub-rows */}
                          {open && r.campaigns && r.campaigns.length > 0 && (
                            <tr>
                              <td colSpan={20} className="p-0 border-t-0">
                                <div className="bg-muted/30">
                                  {/* Sub header */}
                                  <div className="flex items-center gap-1.5 px-2 py-1.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/50 border-b border-border/30">
                                    <div className="w-[26px]" />
                                    <div className="w-[100px]">Version</div>
                                    <div className="w-[48px] text-right">Days</div>
                                    <div className="flex-[2] min-w-0">Campaign</div>
                                    <div className="w-[72px] text-right">Spend</div>
                                    <div className="w-[72px] text-right">Revenue</div>
                                    <div className="w-[52px] text-right">Conv</div>
                                    <div className="w-[72px] text-right">Profit</div>
                                    <div className="w-[56px] text-right">ROI</div>
                                    <div className="w-[60px] text-right">$/Click</div>
                                    <div className="w-[56px] text-right">CPM</div>
                                    <div className="w-[56px] text-right">CPR</div>
                                    <div className="w-[56px] text-right">RPL</div>
                                    <div className="w-[56px] text-right">LP CTR</div>
                                    <div className="w-[56px] text-right">Op CPC</div>
                                    <div className="w-[56px] text-right">Op EPC</div>
                                    <div className="w-[48px] text-right">CVR</div>
                                    <div className="w-[60px] text-right">Signal</div>
                                  </div>
                                  {r.campaigns.map((c, ci) => {
                                    const cd = daysRunning(c.launchDate);
                                    const cSig = c.signal || calcSignal(c);
                                    return (
                                      <div key={ci} className="flex items-center gap-1.5 px-2 py-1.5 font-mono text-[11px] border-b border-border/10 hover:bg-muted/50 transition-colors">
                                        <div className="w-[26px]" />
                                        <div className="w-[100px] text-purple-600 dark:text-purple-400 text-[11px] truncate">{c.version}</div>
                                        <div className="w-[48px] text-right text-muted-foreground" title={c.launchDate ? c.launchDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}>{fmtDays(cd)}</div>
                                        <div className="flex-[2] min-w-0 text-muted-foreground text-[10px] truncate" title={c.campaign}>{c.campaign}</div>
                                        <div className="w-[72px] text-right">{fmt$(c.spend)}</div>
                                        <div className="w-[72px] text-right">{fmt$(c.revenue)}</div>
                                        <div className="w-[52px] text-right">{fmtV(c.conversions)}</div>
                                        <div className={`w-[72px] text-right ${c.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{fmt$(c.profit)}</div>
                                        <div className={`w-[56px] text-right ${c.roi >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{fmtR(c.roi)}</div>
                                        <div className="w-[60px] text-right">{fmt$(c.cpulc)}</div>
                                        <div className="w-[56px] text-right">{fmt$(c.cpm)}</div>
                                        <div className="w-[56px] text-right">{fmt$(c.cpr)}</div>
                                        <div className="w-[56px] text-right">{fmt$(c.rpl)}</div>
                                        <div className="w-[56px] text-right">{fmtN(c.lpctr)}</div>
                                        <div className="w-[56px] text-right">{fmtD(c.opCpc)}</div>
                                        <div className="w-[56px] text-right">{fmtD(c.epc)}</div>
                                        <div className="w-[48px] text-right">{fmtN(c.cvr)}</div>
                                        <div className="w-[60px] text-right"><SignalBadge sig={cSig} /></div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}

                    {/* Page total row */}
                    <tr className="bg-green-50/50 dark:bg-green-950/10 border-t border-green-200/50 dark:border-green-800/30">
                      <td></td>
                      <td className="px-2.5 py-2 text-[9px] font-bold uppercase tracking-wider text-green-700 dark:text-green-400">Page Total</td>
                      <td colSpan={2} className="px-2.5 py-2 text-[11px] text-muted-foreground">{pg.account} · {pg.page} ({pg.rows.length} creatives)</td>
                      <td className="px-2.5 py-2 text-center font-mono text-[11px] text-muted-foreground">{pg.rows.reduce((s, r) => s + r.count, 0)}</td>
                      <td></td>
                      <td className="px-2.5 py-2 font-mono font-bold">{fmt$(ptS)}</td>
                      <td className="px-2.5 py-2 font-mono font-bold">{fmt$(ptR)}</td>
                      <td className="px-2.5 py-2 font-mono font-bold">{fmtV(pg.rows.reduce((s, r) => s + r.conversions, 0))}</td>
                      <td className={`px-2.5 py-2 font-mono font-bold ${ptP >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{fmt$(ptP)}</td>
                      <td className={`px-2.5 py-2 font-mono ${ptROI >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{fmtR(ptROI)}</td>
                      <td colSpan={8}></td>
                      <td className="px-2.5 py-2">
                        <SignalBadge sig={ptROI >= 0.5 ? 'win' : ptROI >= 0.1 ? 'scale' : ptROI < 0 ? 'kill' : 'test'} />
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Diagnostics Modal */}
      {showDiag && stats && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={e => { if (e.target === e.currentTarget) setShowDiag(false); }}>
          <div className="bg-card border border-border rounded-xl w-[820px] max-w-[95vw] max-h-[85vh] flex flex-col shadow-xl">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold">Data Match Diagnostics</h3>
                <p className="text-xs text-muted-foreground mt-1">Comparing Meta campaign names vs RedTrack campaign names</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowDiag(false)}>Close</Button>
            </div>
            <div className="overflow-y-auto p-5 flex-1">
              {stats.noRTList.length > 0 && (
                <div className="mb-5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400 mb-2">
                    ⚠️ {stats.noRTList.length} Meta campaigns with NO match in RedTrack
                  </div>
                  <div className="bg-muted rounded-lg p-3 max-h-[200px] overflow-y-auto font-mono text-[11px]">
                    {stats.noRTList.map((x, i) => (
                      <div key={i} className="py-1 border-b border-border/30 text-red-600 dark:text-red-400">
                        [{x.acct}] {x.metaCamp}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {stats.rtKeys.length > 0 && (
                <div className="mb-5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                    RedTrack campaign names (first 15)
                  </div>
                  <div className="bg-muted rounded-lg p-3 max-h-[200px] overflow-y-auto font-mono text-[11px]">
                    {stats.rtKeys.slice(0, 15).map((x, i) => (
                      <div key={i} className="py-1 border-b border-border/30 text-muted-foreground">
                        [{x.acct}] <span className="text-foreground">{x.raw}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {stats.matchedList.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-green-600 dark:text-green-400 mb-2">
                    ✓ {stats.matchedList.length} matched pairs (first 10)
                  </div>
                  <div className="bg-muted rounded-lg p-3 max-h-[200px] overflow-y-auto font-mono text-[11px]">
                    {stats.matchedList.slice(0, 10).map((x, i) => (
                      <div key={i} className="py-1 border-b border-border/30 text-green-600 dark:text-green-400">
                        [{x.acct}] {x.meta}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── KPI Card ──

const KPI_COLORS: Record<string, string> = {
  blue: 'from-blue-500 to-blue-600',
  green: 'from-green-500 to-green-600',
  red: 'from-red-500 to-red-600',
  yellow: 'from-yellow-500 to-yellow-600',
  purple: 'from-purple-500 to-purple-600',
  cyan: 'from-cyan-500 to-cyan-600',
};

function KPI({ label, value, color, sub }: { label: string; value: string; color: string; sub: string }) {
  return (
    <div className="bg-card border border-border rounded-lg p-3 relative overflow-hidden hover:shadow-sm transition-shadow">
      <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r ${KPI_COLORS[color] || KPI_COLORS.blue}`} />
      <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">{label}</div>
      <div className="text-lg font-bold leading-none tracking-tight">{value}</div>
      <div className="text-[10px] text-muted-foreground mt-1">{sub}</div>
    </div>
  );
}
